import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { parseLinesChart, parseRangeData } from '@emma-helpers/parse-chart-data';
import { getWidgetGroupBys } from '@emma-helpers/widget.helper';
import { OnUnsubscribe } from '@platform/helpers/on-unsubscribe.class';
import { get, set } from 'lodash';

import { EMMAChartComponent } from './charts/emma-chart.component';
import { EMMALinesChartComponent } from './charts/emma-lineschart.component';
import { EMMAStackBarsChartComponent } from './charts/emma-stackbarschart.component';

export interface SelectableKeyChartOption {
  title: string;
  description?: string;
  label?: string;
  field: string;
}
export interface SelectableKeyChartData {
  [k: string]: any;
  report: {
    date: string;
    [k: string]: any;
  }[];
}

@Component({
    selector: 'emma-selectable-key-chart',
    templateUrl: './emma-selectable-key-chart.component.html',
    standalone: false
})
export class EMMASelectableKeyChartComponent extends OnUnsubscribe implements OnChanges {
  @ViewChild(EMMAChartComponent, { static: false })
  chart!: EMMALinesChartComponent | EMMAStackBarsChartComponent;
  // Prop que lleva el nombre de la línea
  @Input() groupField!: string;
  @Input() chartId!: string;
  @Input() options: SelectableKeyChartOption[] = [];
  @Input() selected: string | null = null;
  @Output() selectedChange = new EventEmitter<string | null>();
  @Input() data: SelectableKeyChartData[] = [];
  @Input() dataLabels: string[] = [];
  @Input() sortableLabels = false;
  @Input() sortableLines = true;
  @Input() loading = false;
  @Input() error?: any;
  @Input() removeEmpty = false;
  @Input() typeChart: 'lines' | 'stackbars' = 'lines';
  @Input() fixedField?: string;
  @Input() searchable = true;

  _selectedChart: SelectableKeyChartOption | undefined;
  _groupBy: string | undefined;
  title = '';
  description = '';

  ngOnChanges(changes: SimpleChanges): void {
    if ('selected' in changes) {
      this.setTitle();
    }
    if ('data' in changes || 'loading' in changes) {
      this.whenChartIsReady(() => {
        this.loadData();
      });
    }
    if ('options' in changes) {
      if (!this.selected || !this.getSelectedChart()) {
        this.selected = this.options[0].field;
        this._selectedChart = this.getSelectedChart();
        this.setTitle();
      }
    }
    if ('error' in changes) {
      this.whenChartIsReady(() => this.chart.error(this.error));
    }
    if ('groupField' in changes) {
      const groupBy = getWidgetGroupBys().find((gb) => gb.id === this.groupField);
      this._groupBy = groupBy ? groupBy.title : '';
      this.setTitle();
    }
  }
  whenChartIsReady(fn: any) {
    if (this.chart) {
      fn.call(this);
    } else {
      setTimeout(() => this.whenChartIsReady(fn), 100);
    }
  }
  onChange(selected: string) {
    this._selectedChart = this.getSelectedChart();
    this.setTitle();
    this.selectedChange.next(selected);
  }
  getSelectedChart() {
    return this.selected ? this.options.find((o) => o.field === this.selected) : undefined;
  }
  setTitle() {
    this._selectedChart = this.getSelectedChart();
    if (this._selectedChart) {
      if (this._groupBy) {
        this.title = $localize`${this._selectedChart.title}:title: por ${this._groupBy}:groupBy:`;
      } else {
        this.title = this._selectedChart.title;
      }
      this.description = this._selectedChart.description || this.description;
    }
  }

  getCleanLines() {
    const field = this.fixedField || this.getSelectedChart()?.field || '';
    let data = this.data;
    if (this.removeEmpty) {
      data = data.filter((current: SelectableKeyChartData) =>
        current.report.some((value) => Number(value[field]) !== 0)
      );
    }
    return data?.length ? data : [];
  }

  sortLines(lines: any) {
    const field = this.fixedField || this.getSelectedChart()?.field || '';
    const data = lines.slice(0);
    data.sort((a: any, b: any) => {
      const sumaA = a.report.reduce((sum: number, item: any) => {
        return sum + Number(item?.[field] || 0);
      }, 0);
      const sumaB = b.report.reduce((sum: number, item: any) => {
        return sum + Number(item?.[field] || 0);
      }, 0);
      return sumaB - sumaA;
    });
    return data;
  }

  loadData() {
    const chart =
      this.typeChart === 'lines'
        ? (this.chart as EMMALinesChartComponent)
        : (this.chart as EMMAStackBarsChartComponent);
    this._selectedChart = this.getSelectedChart();
    const dataEmpty = parseLinesChart(
      { values: {}, labels: this.dataLabels },
      { label: '', title: '', index: 0 }
    );
    // Show something if empty
    this.chart.loading();
    if (this._selectedChart) {
      const { _selectedChart } = this;
      if (!this.data?.length) {
        chart.update(dataEmpty);
      } else {
        let data = this.getCleanLines();
        if (this.sortableLines) {
          data = this.sortLines(data);
        }
        if (!data?.length) {
          chart.update(dataEmpty);
          return;
        }
        const field = this.fixedField || _selectedChart.field;
        data.forEach((line, index) => {
          const title: string = get(line, this.groupField);
          const lineChart = parseRangeData(line.report, field, 'date');
          const dataChart = parseLinesChart(
            {
              ...lineChart,
              labels: this.dataLabels,
            },
            {
              label: title,
              title,
              index,
            }
          );
          set(dataChart.graph, 'hidden', index > 2);
          chart.update(dataChart, {
            sortableLabels: this.sortableLabels,
          });
        });
      }
    }
  }
}
